"use client";

import { Alert, Button, Text } from "@softmax/design-system";
import { spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import { StoreEditorRevisionRow } from "./store-editor-revision-row";
import type {
  RevisionReviewActions,
  StoreEditorRevision,
} from "./revision-records";

type StoreEditorHistoryProps = Readonly<{
  revisions: readonly StoreEditorRevision[];
  currentRevisionId: string | null;
  busy: boolean;
  canChangeRevision: boolean;
  failure: Readonly<{ message: string }> | null;
  onRetry(): void;
  reviewActions?: RevisionReviewActions | undefined;
  onRevert(id: string): void;
}>;

export function StoreEditorHistory({
  revisions,
  currentRevisionId,
  busy,
  canChangeRevision,
  failure,
  onRetry,
  reviewActions,
  onRevert,
}: StoreEditorHistoryProps) {
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  function beginRejection(id: string) {
    setReasonFor(id);
    setReason("");
  }

  return (
    <>
      {failure && (
        <Alert tone="danger">
          <span>{failure.message}</span>{" "}
          <Button size="sm" disabled={busy} onClick={onRetry}>
            Retry
          </Button>
        </Alert>
      )}
      {revisions.length === 0 && <Text tone="muted">No revisions yet.</Text>}
      <ol {...stylex.props(styles.list)}>
        {revisions.map((revision) => (
          <StoreEditorRevisionRow
            key={revision.id}
            revision={revision}
            canChangeRevision={canChangeRevision}
            reviewActions={
              revision.id === currentRevisionId &&
              revision.status === "pending_review"
                ? reviewActions
                : undefined
            }
            rejectionReason={reasonFor === revision.id ? reason : null}
            onBeginRejection={beginRejection}
            onReasonChange={setReason}
            onRevert={onRevert}
          />
        ))}
      </ol>
    </>
  );
}

const styles = stylex.create({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.space12,
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
});
